import React, { useCallback, useEffect, useState } from 'react';
import { useGetEventQuery } from '../../../../graphql/queries/getEvent.generated';
import { useGetEventAccountQuery } from '../../../../graphql/queries/getEventAccount.generated';
import { LayoutStoreInstance } from '../../../../LayoutStore';
import i18n from '@/utils/i18n';
import { Button, Input, Table } from 'reactstrap';
import { EventsListWithFilter } from '../../components/EventListWithTimeFilter';
import { useGetFilteredItemLocationsLazyQuery } from '../../../../graphql/queries/getFilteredItemLocations.generated';
import debounce from 'just-debounce-it';
import CustomPagination from '../../../../components/CustomPagination';
import { ContentSection } from '../../../../components/Layout/ContentSection';
import ClassicLoadingSpinner from '../../../../components/LoadingSpinner/ClassicLoadingSpinner';
import { useGetItemLocationTypesQuery } from '../../../../graphql/queries/getItemLocationTypes.generated';
import { SelectItemLocationEventAccount } from './SelectItemLocationEventAccount';

type SelectItemLocationProps = {
  eventId: string | null;
  setEventId: (eventId: string | null) => void;
  eventAccountId: string | null;
  setEventAccountId: (eventAccountId: string | null) => void;
  itemLocationId: string | null;
  setItemLocationId: (itemLocationId: string | null) => void;
  mustSelectEvent: boolean;
  selectEventTitle: string;
  selectEventAccountTitle: string;
  selectItemLocationTitle: string;
  itemLocationName: string | null;
};

const PAGE_SIZE = 5;

export const SelectItemLocation = (props: SelectItemLocationProps) => {
  const {
    eventId,
    setEventId,
    eventAccountId,
    setEventAccountId,
    itemLocationId,
    setItemLocationId,
    mustSelectEvent,
    selectEventTitle,
    selectEventAccountTitle,
    selectItemLocationTitle,
    itemLocationName: itemLocationNameIn,
  } = props;
  const [searchTermItemLocation, setSearchTermItemLocation] = useState('');
  const [itemLocationType, setItemLocationType] = useState('all');
  const [page, setPage] = useState(1);
  const [itemLocationName, setItemLocationName] = useState('');

  const { data: itemLocationTypesResponse, loading: itemLocationTypesLoading } =
    useGetItemLocationTypesQuery();

  const {
    loading: loadingEvent,
    error: errorEvent,
    data: eventData,
  } = useGetEventQuery({
    fetchPolicy: 'no-cache',
    variables: { id: eventId || '' },
    skip: !eventId,
    onCompleted: (data) => setEventId(data.event?.id || null),
  });

  const { error: errorEventAccount } = useGetEventAccountQuery({
    fetchPolicy: 'no-cache',
    variables: { id: eventAccountId || '' },
    skip: !eventAccountId,
    onCompleted: (data) => setEventAccountId(data.eventAccount?.id || null),
  });

  const [
    searchItemLocations,
    { data: searchItemLocationsData, loading: searchItemLocationLoading },
  ] = useGetFilteredItemLocationsLazyQuery();

  const fetchResults = useCallback(
    debounce(
      async (
        text: string,
        eventAccountId: string | null,
        eventId: string | null,
        itemLocationTypeString: string | null,
      ) => {
        if (eventAccountId == null && text.length === 0) {
          return;
        }

        const { data } = await searchItemLocations({
          fetchPolicy: 'no-cache',
          variables: {
            filter: {
              event_account_id: eventAccountId,
              event_id: eventId,
            },
            type:
              itemLocationTypeString !== 'all'
                ? itemLocationTypeString
                : undefined,
            search_term: text,
            skip: (page - 1) * PAGE_SIZE,
            take: PAGE_SIZE,
          },
        });

        if (data == null) {
          return;
        }
      },
      200,
    ),
    [page],
  );

  useEffect(() => {
    if (searchTermItemLocation.length === 0) {
      fetchResults.cancel();
      fetchResults('__', eventAccountId, eventId, itemLocationType);
      return;
    }

    fetchResults.cancel();
    fetchResults(
      searchTermItemLocation,
      eventAccountId,
      eventId,
      itemLocationType,
    );
  }, [searchTermItemLocation, page, eventAccountId, itemLocationType]);

  useEffect(() => {
    if (itemLocationNameIn != null) {
      setItemLocationName(itemLocationNameIn);
    }
  }, [itemLocationNameIn]);

  if (errorEvent) {
    LayoutStoreInstance.setError('Error loading event: ' + errorEvent);
  }

  if (errorEventAccount) {
    LayoutStoreInstance.setError('Error loading event account: ' + errorEvent);
  }

  console.log({ itemLocationNameIn, itemLocationName });

  const paginationResults =
    searchItemLocationsData?.getFilteredItemLocations.total || 0;
  const pageCount = Math.ceil(paginationResults / PAGE_SIZE);

  function getTitle(ogTitle: string, mustSelect: boolean) {
    let title = ogTitle;
    if (!mustSelect) {
      title += ' (' + i18n.t('Common:optional') + ')';
    }
    return title;
  }

  function handleItemLocationClick(itemLocation: {
    id: string;
    name: string;
    event_account_id?: string | null;
  }) {
    setItemLocationId(itemLocation.id);
    setItemLocationName(itemLocation.name);
    setEventAccountId(itemLocation.event_account_id || null);
  }

  console.log({ eventData });

  const itemLocationTypes = itemLocationTypesResponse?.getItemLocationTypes;
  const eventName = eventData?.event?.name;
  const hasEventName = eventName != null;
  const hasItemLocation = !!itemLocationId;

  return (
    <div>
      <ContentSection
        title={getTitle(selectEventTitle, mustSelectEvent)}
        collapsable={false}
      >
        {i18n.t('CreateOrderFulfillment:selectEventDescription')}

        {hasEventName && (
          <>
            <h3 className="mt-4 mb-4">
              {i18n.t('CreateOrderFulfillment:selected')}
            </h3>
            <b>
              <Button
                onClick={() => {
                  setEventId(null);

                  setEventAccountId(null);
                  setItemLocationId(null);
                  setItemLocationName('');
                }}
                color="primary"
              >
                {eventName}
              </Button>
            </b>
          </>
        )}

        {!hasEventName && (
          <>
            <h3 className="mt-4 mb-4">
              {i18n.t('CreateOrderFulfillment:selection')}
            </h3>
            <EventsListWithFilter showSelect setEventId={setEventId} />
          </>
        )}
      </ContentSection>

      <SelectItemLocationEventAccount
        eventData={eventData}
        eventAccountId={eventAccountId}
        setEventAccountId={setEventAccountId}
        setItemLocationId={setItemLocationId}
        setItemLocationName={(name: string) => {
          setItemLocationName(name);
        }}
        selectEventAccountTitle={selectEventAccountTitle}
      />

      <ContentSection title={selectItemLocationTitle} collapsable={false}>
        {i18n.t('CreateOrderFulfillment:selectItemLocationDescription')}

        {hasItemLocation && (
          <>
            <h3 className="mt-4 mb-4">
              {i18n.t('CreateOrderFulfillment:selected')}
            </h3>
            <b>
              <Button
                color="primary"
                onClick={() => {
                  setItemLocationId(null);
                  setItemLocationName('');
                }}
              >
                {itemLocationName}
              </Button>
            </b>
          </>
        )}

        {!hasItemLocation && (
          <>
            <h3 className="mt-4 mb-4">
              {i18n.t('CreateOrderFulfillment:selection')}
            </h3>
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('Common:search')}
              </span>
              <input
                type="text"
                className="form-control"
                placeholder={i18n.t('Common:search')}
                value={searchTermItemLocation}
                onChange={(e) => {
                  setSearchTermItemLocation(e.target.value);
                }}
              />
            </div>
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('Common:type')}
              </span>
              <Input
                type="select"
                value={itemLocationType}
                onChange={(e) => {
                  setItemLocationType(e.currentTarget.value);
                }}
                disabled={itemLocationTypesLoading}
              >
                <option>all</option>
                {itemLocationTypes?.map(({ type }) => {
                  return <option>{type}</option>;
                })}
              </Input>
            </div>
            <br />
            <Table striped hover bordered>
              <thead>
                <tr>
                  <th>{i18n.t('Common:name')}</th>
                  <th>{i18n.t('Common:type')}</th>
                </tr>
              </thead>
              <tbody>
                {searchItemLocationLoading && (
                  <tr>
                    <ClassicLoadingSpinner />
                  </tr>
                )}
                {!searchItemLocationLoading &&
                  (!searchItemLocationsData?.getFilteredItemLocations
                    .item_locations ||
                    searchItemLocationsData?.getFilteredItemLocations
                      .item_locations?.length === 0) && (
                    <tr>
                      <td>-</td>
                      <td>-</td>
                    </tr>
                  )}
                {searchItemLocationsData?.getFilteredItemLocations.item_locations.map(
                  (itemLocation) => (
                    <tr key={itemLocation.id}>
                      <td>
                        <Button
                          onClick={() => handleItemLocationClick(itemLocation)}
                          color={
                            itemLocation.id === itemLocationId
                              ? 'primary'
                              : 'secondary'
                          }
                        >
                          {itemLocation.name}
                        </Button>
                      </td>
                      <td>{itemLocation.type || 'Event'}</td>
                    </tr>
                  ),
                )}
              </tbody>
            </Table>

            {paginationResults > PAGE_SIZE && (
              <CustomPagination
                pageCount={pageCount}
                currentPage={page}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </ContentSection>
    </div>
  );
};
